import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { Button } from '…/app/common/Button/Button.tsx';

import type {
  Audience,
} from './Audience.d.ts';

// Define schema for form validation
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

type FormValues = z.infer<typeof formSchema>;

export function UpsertAudienceForm({
  action,
  audience: {
    id,
    name,
  } = {
    id: '',
    name: '',
  },
  onSubmit,
}: {
  action: Action,
  audience?: Pick<Audience, 'id' | 'name'>,
  onSubmit(name: Audience['name']): Promise<void>,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const isNew = !id;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: name || '',
    },
    resolver: zodResolver(formSchema),
  });

  // Handle the special case where we have a name but no id (cloning)
  const isFormDirty = isDirty || (!id && !!name);

  async function handleFormSubmit(data: FormValues) {
    setIsSaving(true);

    const promise = onSubmit(data.name).finally(() => setIsSaving(false));

    toast.promise(promise, {
      error: 'Saving audience failed',
      loading: 'Saving audience…',
      success: `"${data.name}" saved`,
    });

    return promise;
  }

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <h1>{ACTION_HEADINGS[action]} Audience</h1>

      <div className="space-y-2">
        <label
          className="block text-sm font-medium text-gray-700"
          htmlFor="name"
        >
          Name
        </label>

        <input
          className={`w-full px-3 py-3 border border-gray-400 border-solid rounded-md appearance-none outline-none focus:outline-none focus:border-blue-500 ${errors.name ? 'border-red-500' : ''}`}
          id="name"
          placeholder="My new Orbiit Audience"
          type="text"
          {...register('name')}
        />

        {errors.name && (
          <p className="text-red-500 text-sm">{errors.name.message}</p>
        )}
      </div>

      <Button
        className="w-full"
        disabled={isSaving || !isFormDirty}
        loading={isSaving}
        type="submit"
        variant={isNew ? 'secondary' : 'primary'}
      >{isNew ? 'Create' : 'Save changes'}
      </Button>
    </form>
  );
}
UpsertAudienceForm.displayName = 'UpsertAudienceForm';

const ACTION_HEADINGS = {
  clone: 'Copy',
  create: 'Create',
  rename: 'Rename',
};
type Action = keyof typeof ACTION_HEADINGS;
